import React, { useCallback, useEffect, useRef, useState } from "react";
import "@/App.css";
import { Toaster, toast } from "sonner";
import { Plus } from "lucide-react";

import MorningCheckIn from "@/components/MorningCheckIn";
import QuickAddSheet from "@/components/QuickAddSheet";
import StatsBar from "@/components/StatsBar";
import SettingsPanel from "@/components/SettingsPanel";
import CheckInPopup from "@/components/CheckInPopup";
import CalendarView from "@/components/CalendarView";
import ChatWidget from "@/components/ChatWidget";
import MochiWaitedOverlay from "@/components/MochiWaitedOverlay";
import FireDanceOverlay from "@/components/FireDanceOverlay";
import MochiHero from "@/components/MochiHero";
import OverwhelmButton from "@/components/OverwhelmButton";
import OverwhelmOverlay from "@/components/OverwhelmOverlay";
import RestedReminderPrompt from "@/components/RestedReminderPrompt";
import MilestoneCelebration from "@/components/MilestoneCelebration";
import SuggestButton from "@/components/SuggestButton";
import { PomodoroTimer } from "@/components/PomodoroTimer";
import StatsPage from "@/components/StatsPage";
import Header from "@/components/Header";
import TabbedLists from "@/components/TabbedLists";
import QuickCapture from "@/components/QuickCapture";
import SignInScreen from "@/components/auth/SignInScreen";
import ForgotPasswordScreen from "@/components/auth/ForgotPasswordScreen";
import ResetPasswordScreen from "@/components/auth/ResetPasswordScreen";

import {
    fetchSettings, updateSettings,
    requestAvatarMessage, fetchStats, restReminder, fetchMe,
    unlockAllAccessories, fetchBypassStatus, devLogin,
} from "@/lib/api";
import { TODAY } from "@/lib/dates";
import { maybeAskNotificationPermission } from "@/lib/notifications";
import { summarizeAndToastChatActions } from "@/lib/chatActionToasts";

import { useTheme } from "@/hooks/useTheme";
import { useItems } from "@/hooks/useItems";
import { useReminderPolling } from "@/hooks/useReminderPolling";
import { useReminderNervousness } from "@/hooks/useReminderNervousness";
import { usePeriodicCheckin } from "@/hooks/usePeriodicCheckin";
import { useGlobalSearch } from "@/hooks/useGlobalSearch";
import { useMochiMood } from "@/hooks/useMochiMood";
import { usePomodoro } from "@/hooks/usePomodoro";
import { useMorningBootstrap } from "@/hooks/useMorningBootstrap";
import { useSkinUnlock } from "@/hooks/useSkinUnlock";
import { useIdleGlance, isIdleCompatible } from "@/hooks/useIdleGlance";
import { useMilestoneWatcher } from "@/hooks/useMilestoneWatcher";
import { useMochiPick } from "@/hooks/useMochiPick";
import { useCoverPassFlow } from "@/hooks/useCoverPassFlow";
import { useOverwhelmFlow } from "@/hooks/useOverwhelmFlow";
import { useQuickCaptureFlow } from "@/hooks/useQuickCaptureFlow";
import { useRecentFocus } from "@/hooks/useRecentFocus";
import { useMeterRefresh } from "@/hooks/useMeterRefresh";
import { useHungerDrop } from "@/hooks/useHungerDrop";
import { useReleaseNotes } from "@/hooks/useReleaseNotes";
import { useSearchRowHandlers } from "@/hooks/useSearchRowHandlers";

// Tiny one-liners Mochi whispers when the user starts ANOTHER focus session
// on a task that's still in the recently-focused window. Pure UI sugar.
const RECENT_FOCUS_WHISPERS = [
    "oh this one again — we're getting good at this 💪",
    "back to this? love the consistency ✨",
    "round two. let's finish what we started 🌟",
    "you really mean it with this one, huh? 🍵",
    "same task, fresher brain. let's go 🧠",
];

function MochiApp({ me }) {
    const today = TODAY();
    const { theme, toggleTheme } = useTheme();

    // ---- UI shell state --------------------------------------------------
    const [activeTab, setActiveTab] = useState("task");
    const [viewMode, setViewMode] = useState("list"); // 'list' | 'calendar'
    const [quickOpen, setQuickOpen] = useState(false);
    const [quickDefaultDate, setQuickDefaultDate] = useState(null);
    // When the user taps a task/habit/reminder/list row, the Quick Add sheet
    // opens in EDIT mode pre-filled with that item's data. `null` = creating;
    // `{ kind, item }` = editing.
    const [editingItem, setEditingItem] = useState(null);
    const [settingsOpen, setSettingsOpen] = useState(false);
    const [statsOpen, setStatsOpen] = useState(false);
    const [morningOpen, setMorningOpen] = useState(false);
    const [morningGreeting, setMorningGreeting] = useState("");

    const [checkin, setCheckin] = useState({ open: false, message: "", mood: "happy", kind: null, title: null, reminderId: null, taskId: null });
    const lastCheckinRef = useRef(0);
    // Gate the next periodic ping. Updated on every successful ping (to
    // `now + interval`) and on "Snooze 30m" (to `now + 30min`).
    const nextEligibleAtRef = useRef(0);
    // Ref to the avatar wrapper so the rice ball can fly to Mochi's center
    const avatarRef = useRef(null);

    // "Mochi waited for you" intro overlay — shown when user returns after ≥1 day away
    const [waitedOverlay, setWaitedOverlay] = useState({ open: false, daysAway: 0 });
    // Fire dance overlay — fires for 30s when Mochi's Hype Meter hits Inferno
    const [fireDance, setFireDance] = useState(false);
    // Chat-open tracker — used to suppress idle ambient anims while chatting.
    const [chatOpen, setChatOpen] = useState(false);

    const [mochiPick, setMochiPick] = useMochiPick();
    const { recentFocusMap, handleFocusComplete } = useRecentFocus();

    // ---- mood / speech bubble (decoupled from data) ----------------------
    // Two-pass: declare mood with placeholder baseMood, then re-declare items
    // afterwards. To avoid a circular ref we pass a getter-style stats reference
    // by computing displayMood after items load. We hoist useMochiMood with
    // `null` placeholders updated each render — React handles re-derivation.
    // NB: items still drives celebrate, but celebrate is a stable callback.
    const items = useItems({
        today,
        onCelebrate: (...args) => mood.celebrate(...args),
    });
    const {
        settings, setSettings, stats,
        tasks, todaysHabits, reminders, listItems,
        setReminders,
        loadAll, refreshStats,
        handleToggleTask, handleDeleteTask, handleEditTask,
        handleToggleHabit, handleDeleteHabit, handleEditHabit, handleCoverHabit,
        handleToggleReminder, handleDeleteReminder, handleEditReminder,
        handleToggleList, handleDeleteList, handleEditList,
        handleQuickSubmit,
    } = items;

    // Meter drains over 48h since last feed — keep it fresh.
    useMeterRefresh(setSettings);

    // Soft Mochi-voice toast when the Hunger Meter drops a stage (drains
    // organically from the 48h-without-feeding decay). Rises are silent.
    useHungerDrop({ settings });

    // Nervousness escalator while a reminder popup is open
    const nervousness = useReminderNervousness({
        active: checkin.open && checkin.kind === "reminder",
        onBubble: (m) => mood.setBubbleMsg(m),
    });

    const mood = useMochiMood({
        baseMood: stats?.emotion || "happy",
        nervousness,
        stats,
    });

    // ---- search ----------------------------------------------------------
    const search = useGlobalSearch();
    const { handleSearchToggle, handleSearchDelete } = useSearchRowHandlers({ search, refreshStats });

    // ---- avatar skins (sunglasses unlock at 10 pomodoros) ----------------
    const { skin, animateSkinIn } = useSkinUnlock(settings);

    // ---- bootstrap (initial load + morning check-in + weekly recap) ------
    useMorningBootstrap({
        today,
        loadAll,
        idleMessage: mood.idleMessage,
        setBubbleMsg: mood.setBubbleMsg,
        setBubbleLoading: mood.setBubbleLoading,
        setMorningGreeting,
        setMorningOpen,
        onReturnFromAbsence: (daysAway) => setWaitedOverlay({ open: true, daysAway }),
    });

    // ---- periodic check-ins (gentle/balanced/accountability) -------------
    const onPeriodicCheckin = useCallback(({ message, mood: m, kind, taskId }) => {
        setCheckin({ open: true, message, mood: m, kind: kind || null, title: null, reminderId: null, taskId: taskId || null });
    }, []);
    usePeriodicCheckin({ settings, tasks, lastCheckinRef, nextEligibleAtRef, onCheckin: onPeriodicCheckin });

    const handleSnoozeAccountability = useCallback(() => {
        nextEligibleAtRef.current = Date.now() + 30 * 60 * 1000;
        setCheckin((c) => ({ ...c, open: false }));
        toast("🍡 Mochi will check back in 30 min", { duration: 3500 });
    }, []);

    // ---- global quick-capture (Cmd+K / floating button) -------------------
    const { captureOpen, setCaptureOpen, tabPulse, handleCaptured } = useQuickCaptureFlow({
        loadAll, refreshStats, mood, setViewMode, setActiveTab,
    });

    // "Different task" picker — user explicitly picks the task they want
    // Mochi to nag about next. Re-fetches a personalised AI line for that
    // task and patches the popup in place.
    const handlePickTask = useCallback(async (taskId) => {
        if (!taskId) return;
        const next = tasks.find((t) => t.id === taskId && !t.completed);
        if (!next) {
            toast("That task isn't open anymore 🍡", { duration: 3000 });
            return;
        }
        try {
            const fresh = await fetchStats().catch(() => null);
            const r = await requestAvatarMessage("accountability_ping", fresh?.emotion || "determined", {
                tasks_done: fresh?.tasks_done, tasks_total: fresh?.tasks_total,
                habits_done: fresh?.habits_done, habits_total: fresh?.habits_total,
                task_title: next.title,
            });
            setCheckin((c) => ({ ...c, message: r.message, taskId: next.id }));
        } catch {
            setCheckin((c) => ({ ...c, taskId: next.id }));
            toast(`Got it — switched to "${next.title}" 🍡`, { duration: 3000 });
        }
    }, [tasks]);

    // ---- reminder polling ------------------------------------------------
    const onReminderDue = useCallback(({ id, title, body, kind }) => {
        // 15-min heads-up uses a softer "worried" face; the actual due ring is energetic.
        const popupMood = kind === "pre" ? "worried" : "energetic";
        setCheckin({ open: true, message: body, mood: popupMood, kind: "reminder", title, reminderId: id });
        lastCheckinRef.current = Date.now();
    }, []);
    useReminderPolling({ today, onReminderDue, onRefreshReminders: setReminders });

    // ---- pomodoro --------------------------------------------------------
    const pom = usePomodoro({ settings, setSettings, onCheckin: setCheckin, onFocusComplete: handleFocusComplete });

    // ---- overwhelm panic flow ---------------------------------------------
    const overwhelm = useOverwhelmFlow({ mood, handleToggleTask, setMochiPick });

    // ---- cover-pass flow ---------------------------------------------------
    const { coverOffer, handleAcceptCoverPass, handleDeclineCoverPass, askMochiToCover } =
        useCoverPassFlow({ mood, handleCoverHabit, todaysHabits, settings });

    // Idle micro-glance: pupils briefly shift left/right after 30s inactivity
    // when Mochi is in an idle-compatible mood and no major UI surface is up.
    const glanceActive =
        isIdleCompatible(mood.displayMood) &&
        !chatOpen &&
        !pom.pomodoro &&
        !overwhelm.overwhelmOpen &&
        !checkin.open &&
        !coverOffer.open;

    // ---- milestone memory --------------------------------------------------
    // Gate the watcher on settings being loaded + no other heavy surface up.
    // The watcher itself ensures at most one milestone per session and shows
    // the smallest pending milestone first if multiple are due.
    const milestoneEnabled =
        !!settings &&
        !overwhelm.overwhelmOpen &&
        !coverOffer.open;
    const milestoneWatcher = useMilestoneWatcher({
        settings,
        enabled: milestoneEnabled,
        onMoodOverride: (m, durationMs) => mood.forceMoodFor?.(m, durationMs),
        onSettingsUpdate: setSettings,
    });
    const milestoneActive = !!milestoneWatcher.milestone;
    // Per spec: if a milestone and the morning check-in both want to fire
    // on the same day, let the milestone LAND first. We close the morning
    // sheet while a milestone is active; it can re-open after completion.
    useEffect(() => {
        if (milestoneActive && morningOpen) {
            setMorningOpen(false);
        }
    }, [milestoneActive, morningOpen]);

    // Idle glance also pauses during a milestone celebration.
    const idleGaze = useIdleGlance({ active: glanceActive && !milestoneActive });

    // One-time "what's new" toast — fires once per device on the next visit
    // after a meaningful release. Gated on no competing overlay so it lands
    // cleanly on its own.
    useReleaseNotes({
        settings,
        anyOverlayOpen:
            morningOpen ||
            overwhelm.overwhelmOpen ||
            milestoneActive ||
            coverOffer.open ||
            waitedOverlay.open ||
            checkin.open,
    });

    // Wrap startFocus so Mochi whispers a tiny one-liner whenever the user
    // starts ANOTHER focus session on a recently-focused task.
    const handleStartFocus = useCallback((task, focusMin, breakMin) => {
        try {
            if (task?.id && recentFocusMap?.[task.id]) {
                const line = RECENT_FOCUS_WHISPERS[Math.floor(Math.random() * RECENT_FOCUS_WHISPERS.length)];
                mood.setBubbleMsg?.(line);
            }
        } catch (e) {
            console.warn("focus whisper failed (never blocks the session)", e);
        }
        pom.startFocus(task, focusMin, breakMin);
    }, [pom, recentFocusMap, mood]);

    // Bumped whenever items change OUTSIDE CalendarView's own handlers
    // (QuickAddSheet, chat actions) so the calendar refetches its month data
    // immediately instead of waiting for a remount.
    const [calRefreshKey, setCalRefreshKey] = useState(0);

    const handleQuickSubmitAndRefresh = useCallback(async (...args) => {
        const res = await handleQuickSubmit(...args);
        setCalRefreshKey((n) => n + 1);
        return res;
    }, [handleQuickSubmit]);

    // Click-to-edit: tapping a row's central content area opens the Quick Add
    // sheet pre-filled with that item's data. One unified entry point for all
    // four item types so the user never has to learn separate edit gestures.
    const handleOpenEdit = useCallback((kind, item) => {
        setEditingItem({ kind, item });
        setQuickDefaultDate(null);
        setQuickOpen(true);
    }, []);

    // Quick Add's `onSave` (edit path) dispatches to the kind-appropriate
    // useItems handler. Each `handleEdit*` already updates local state and
    // refreshes stats; we just need to bump the calendar key.
    const handleEditSave = useCallback(async (kind, id, patch) => {
        // useItems' handleEdit* take (item, patch) — pass the live item from
        // editingItem so the wins / streak math etc. see the prior state.
        const live = editingItem?.item || { id };
        try {
            if (kind === "task")     await handleEditTask(live, patch);
            else if (kind === "habit")    await handleEditHabit(live, patch);
            else if (kind === "reminder") await handleEditReminder(live, patch);
            else if (kind === "list")     await handleEditList(live, patch);
            setCalRefreshKey((n) => n + 1);
            toast.success("Saved ✨", { duration: 2000 });
        } catch (e) {
            console.error("edit save failed", e);
            toast.error("Couldn't save those changes 🍡");
        } finally {
            setEditingItem(null);
        }
    }, [editingItem, handleEditTask, handleEditHabit, handleEditReminder, handleEditList]);

    const handleSettingsUpdate = async (patch) => {
        const s = await updateSettings(patch);
        setSettings(s);
        if (patch.notification_mode) toast.success(`Mode: ${patch.notification_mode}`);
    };

    const handleUnlockAll = async () => {
        const s = await unlockAllAccessories();
        setSettings(s);
        toast.success("Everything unlocked — Mochi is fully decked out 🍡👑");
    };

    const closeMorning = async () => {
        await updateSettings({ morning_checkin_date: today });
        const s = await fetchSettings();
        setSettings(s);
        await loadAll();
        setMorningOpen(false);
        mood.setBubbleMsg("Day's all set up! Let's go 🌸");
        mood.idleMessage();
        try {
            if (reminders.length > 0) await maybeAskNotificationPermission();
        } catch (e) {
            console.warn("notification permission ask failed", e);
        }
    };

    // ---- chat action handler ---------------------------------------------
    const handleChatActions = useCallback(async (actions) => {
        await loadAll();
        setCalRefreshKey((n) => n + 1);
        if (actions.some((a) => a.type === "easter_egg")) {
            mood.setBubbleMsg("(secret confetti. I love you too.) 🍡");
            mood.forceMoodFor("party");
            toast("🎉 🍡 🎉", {
                duration: 4000,
                style: { fontSize: "24px", textAlign: "center" },
            });
        }
        if (actions.some((a) => a.type === "summon_celebration")) {
            mood.summonCelebration();
        }
        const counts = summarizeAndToastChatActions(actions);
        if (counts.add_reminder) {
            await maybeAskNotificationPermission();
        }
    }, [loadAll, mood]);

    return (
        <div className="App font-body">
            <MochiWaitedOverlay
                open={waitedOverlay.open}
                daysAway={waitedOverlay.daysAway}
                onDismiss={() => setWaitedOverlay((w) => ({ ...w, open: false }))}
            />
            <FireDanceOverlay
                active={fireDance}
                onDone={() => setFireDance(false)}
                onLine={(line) => mood.setBubbleMsg?.(line)}
            />
            <Toaster
                position="top-center"
                theme={theme}
                toastOptions={theme === "dark" ? {
                    style: { background: "#2F2724", color: "#F4E8DC", border: "1px solid rgba(244,232,220,0.12)", fontFamily: "Nunito" },
                } : {
                    style: { background: "#FDFBF7", color: "#4A3E3B", border: "1px solid rgba(74,62,59,0.1)", fontFamily: "Nunito" },
                }}
            />
            <div
                className={`max-w-md mx-auto min-h-screen [min-height:100dvh] px-4 sm:px-5 pb-32 pt-[max(env(safe-area-inset-top),16px)] safe-x relative transition-opacity duration-[400ms] ${overwhelm.dimWorld ? "opacity-20" : "opacity-100"}`}
                aria-hidden={overwhelm.dimWorld ? "true" : undefined}
            >
                <Header
                    avatarName={settings?.avatar_name}
                    theme={theme}
                    onToggleTheme={toggleTheme}
                    onOpenStats={() => setStatsOpen(true)}
                    viewMode={viewMode}
                    onToggleView={() => setViewMode((v) => (v === "list" ? "calendar" : "list"))}
                    onOpenSettings={() => setSettingsOpen(true)}
                />

                <MochiHero
                    avatarRef={avatarRef}
                    mood={mood}
                    settings={settings}
                    setSettings={setSettings}
                    skin={skin}
                    animateSkinIn={animateSkinIn}
                    fireDance={fireDance}
                    onFireDance={() => setFireDance(true)}
                    idleGaze={idleGaze}
                    coverOffer={coverOffer}
                    onAcceptCover={handleAcceptCoverPass}
                    onDeclineCover={handleDeclineCoverPass}
                />

                <div className="mt-6">
                    <StatsBar
                        stats={stats}
                        onOpenStats={() => setStatsOpen(true)}
                        onSummonCelebration={mood.summonCelebration}
                    />
                </div>

                {pom.pomodoro && (
                    <div className="mt-4">
                        <PomodoroTimer
                            session={pom.pomodoro}
                            onComplete={pom.handleComplete}
                            onCancel={pom.cancel}
                        />
                    </div>
                )}

                {viewMode === "list" && (
                    <>
                        <div className="mb-2 flex items-center justify-between gap-2">
                            <RestedReminderPrompt
                                open={!morningOpen}
                                onReschedule={() => toast("tap the time on the row to reschedule 🌸", { duration: 3500 })}
                            />
                            <SuggestButton
                                onLoading={(busy) => {
                                    if (busy) {
                                        mood.setBubbleMsg?.("hmm… let me think 🍡");
                                        mood.forceMoodFor?.("excited", 1200);
                                    }
                                }}
                                onPick={(data) => {
                                    // Update Mochi's bubble + golden glow + brief excited state.
                                    mood.setBubbleMsg?.(data?.message || "");
                                    if (data?.task_id) {
                                        setMochiPick({
                                            task_id: data.task_id,
                                            task_title: data.task_title,
                                            message: data.message,
                                            created_at: Date.now(),
                                        });
                                        mood.forceMoodFor?.("excited", 2400);
                                    }
                                }}
                            />
                        </div>
                        <TabbedLists
                            today={today}
                            activeTab={activeTab}
                            onTabChange={setActiveTab}
                            query={search.query}
                            onQueryChange={search.setQuery}
                            q={search.q}
                            searchLoading={search.loading}
                            groupedResults={search.groupedResults}
                            matchCounts={search.matchCounts}
                            onSearchToggle={handleSearchToggle}
                            onSearchDelete={handleSearchDelete}
                            tasks={tasks}
                            todaysHabits={todaysHabits}
                            reminders={reminders}
                            listItems={listItems}
                            onToggleTask={overwhelm.handleToggleTaskWithOverwhelm}
                            onDeleteTask={handleDeleteTask}
                            onEditTask={handleEditTask}
                            onOpenEditTask={(t) => handleOpenEdit("task", t)}
                            mochiPickTaskId={mochiPick?.task_id || null}
                            onToggleHabit={handleToggleHabit}
                            onDeleteHabit={handleDeleteHabit}
                            onEditHabit={handleEditHabit}
                            onOpenEditHabit={(h) => handleOpenEdit("habit", h)}
                            onAskCoverHabit={settings?.cover_pass_available ? askMochiToCover : undefined}
                            onToggleReminder={handleToggleReminder}
                            onDeleteReminder={handleDeleteReminder}
                            onEditReminder={handleEditReminder}
                            onOpenEditReminder={(r) => handleOpenEdit("reminder", r)}
                            onToggleList={handleToggleList}
                            onDeleteList={handleDeleteList}
                            onEditList={handleEditList}
                            onOpenEditList={(i) => handleOpenEdit("list", i)}
                            onStartFocus={handleStartFocus}
                            pomodoro={pom.pomodoro}
                            recentFocusMap={recentFocusMap}
                            inFocusTaskId={overwhelm.inFocusTaskId}
                            avatarStyle={settings?.avatar_style || "feminine"}
                            tabPulse={tabPulse}
                        />
                    </>
                )}

                {viewMode === "calendar" && (
                    <div className="mt-5">
                        <CalendarView
                            onChange={refreshStats}
                            refreshKey={calRefreshKey}
                            onAddForDate={(d) => { setQuickDefaultDate(d); setQuickOpen(true); }}
                            onOpenEditTask={(t) => handleOpenEdit("task", t)}
                            onOpenEditHabit={(h) => handleOpenEdit("habit", h)}
                            onOpenEditReminder={(r) => handleOpenEdit("reminder", r)}
                        />
                    </div>
                )}

                <button
                    onClick={() => setQuickOpen(true)}
                    className="fixed left-1/2 -translate-x-1/2 z-30 bg-terracotta hover:bg-terracotta-hover text-cream font-display text-base px-6 py-3.5 rounded-full shadow-float hover:-translate-y-0.5 transition-all inline-flex items-center gap-2"
                    style={{ bottom: "calc(max(env(safe-area-inset-bottom), 0px) + 72px)" }}
                    data-testid="fab-add"
                >
                    <Plus size={18} /> Add new
                </button>
            </div>

            <QuickCapture
                open={captureOpen}
                onClose={() => setCaptureOpen(false)}
                avatarStyle={settings?.avatar_style || "feminine"}
                onCaptured={handleCaptured}
            />

            <MorningCheckIn
                open={morningOpen}
                onComplete={closeMorning}
                greetingMessage={morningGreeting}
                avatarStyle={settings?.avatar_style || "feminine"}
                displayName={me?.display_name || ""}
            />
            <QuickAddSheet
                open={quickOpen}
                onClose={() => { setQuickOpen(false); setQuickDefaultDate(null); setEditingItem(null); }}
                defaultType={activeTab}
                defaultDate={quickDefaultDate || undefined}
                onSubmit={handleQuickSubmitAndRefresh}
                editing={editingItem}
                onSave={handleEditSave}
            />
            <SettingsPanel
                open={settingsOpen}
                onClose={() => setSettingsOpen(false)}
                settings={settings}
                onUpdate={handleSettingsUpdate}
                isAdmin={!!me?.is_admin}
                isBetaAdmin={!!me?.is_beta_admin}
                bypassAllowed={!!me?.login_bypass_allowed}
                onUnlockAll={handleUnlockAll}
            />
            <StatsPage
                open={statsOpen}
                onClose={() => setStatsOpen(false)}
                avatarStyle={settings?.avatar_style || "feminine"}
                avatarSkin={skin}
                onAcceptTinyTask={(t) => {
                    setActiveTab("task");
                    setStatsOpen(false);
                    toast(`Mochi picked: ${t.title} 🍡`, { duration: 4000 });
                }}
            />
            <CheckInPopup
                open={checkin.open}
                onDismiss={() => {
                    // For reminder popups, an explicit dismiss = intentional
                    // "i don't need this right now" → move straight to
                    // "rested for today" (no auto-snooze, no follow-up nudge).
                    if (checkin.kind === "reminder" && checkin.reminderId) {
                        restReminder(checkin.reminderId).catch((e) => console.warn("rest reminder failed", e));
                    }
                    setCheckin((c) => ({ ...c, open: false }));
                }}
                message={checkin.message}
                mood={checkin.mood}
                avatarStyle={settings?.avatar_style || "feminine"}
                kind={checkin.kind}
                title={checkin.title}
                onMarkDone={
                    checkin.kind === "reminder" && checkin.reminderId
                        ? async () => {
                              const r = reminders.find((x) => x.id === checkin.reminderId);
                              if (r) await handleToggleReminder(r);
                              setCheckin((c) => ({ ...c, open: false }));
                          }
                        : undefined
                }
                onSnooze={checkin.kind === "accountability" ? handleSnoozeAccountability : undefined}
                onPickTask={checkin.kind === "accountability" ? handlePickTask : undefined}
                onStartFocusTask={
                    checkin.kind === "accountability"
                        ? (task) => {
                              setCheckin((c) => ({ ...c, open: false }));
                              handleStartFocus(task, 25, 5);
                          }
                        : undefined
                }
                pickerTasks={
                    checkin.kind === "accountability"
                        ? tasks.filter((t) => !t.completed && t.id !== checkin.taskId)
                        : []
                }
            />
            <ChatWidget
                avatarStyle={settings?.avatar_style || "feminine"}
                avatarSkin={skin}
                mood={mood.displayMood}
                botName={settings?.avatar_name || "Mochi"}
                onActions={handleChatActions}
                onOpenChange={setChatOpen}
            />
            {/* Persistent panic pill — always visible, always reachable.
                Lives outside the dim wrapper so it stays crisp when the
                world fades to 20% during the flow. */}
            <OverwhelmButton
                onOpen={overwhelm.openOverwhelm}
                avatarStyle={settings?.avatar_style || "feminine"}
            />
            <OverwhelmOverlay
                open={overwhelm.overwhelmOpen}
                tasks={tasks}
                habits={todaysHabits}
                todayKey={today}
                avatarStyle={settings?.avatar_style || "feminine"}
                onPickAndStart={overwhelm.startOverwhelmTask}
                onJustAMinute={overwhelm.dismissOverwhelmGently}
            />
            {/* Milestone celebration — fires at session start on day 7/30/100/365.
                Sits above everything (z-50) so it lands before the morning
                check-in even when both want to open the same day. */}
            <MilestoneCelebration
                open={milestoneActive}
                milestone={milestoneWatcher.milestone}
                phrase={milestoneWatcher.phrase}
                durationMs={milestoneWatcher.milestone?.celebrationMs}
                avatarStyle={settings?.avatar_style || "feminine"}
                skin={skin}
                onComplete={milestoneWatcher.complete}
            />
        </div>
    );
}

// Fire-and-forget rest call used by the check-in popup dismiss path.
function restReminderSafely(reminderId) {
    import("@/lib/api").then(({ restReminder }) => restReminder(reminderId)).catch((e) => {
        console.warn("rest reminder failed", e);
    });
}

// Auth gate — the sign-in screen is the FIRST thing every visitor sees.
// The main app never mounts (and never fetches data) until the session
// resolves to a registered account.
//
// Three pre-auth screens live in this gate:
//   1. SignInScreen (default)
//   2. ForgotPasswordScreen (toggled via "forgot password?" link)
//   3. ResetPasswordScreen (auto-shown when the URL is /reset-password?token=…)
export default function App() {
    const [me, setMe] = useState(null); // null = checking session
    // "signin" | "forgot" | "reset"
    const [authView, setAuthView] = useState(() => {
        // Detect the reset-link URL on first render so we land directly on
        // the reset form without a flash of the sign-in screen.
        try {
            const path = window.location.pathname || "";
            const params = new URLSearchParams(window.location.search || "");
            if (path === "/reset-password" && params.get("token")) return "reset";
        } catch { /* SSR safe */ }
        return "signin";
    });
    const resetToken = (() => {
        try {
            return new URLSearchParams(window.location.search || "").get("token") || "";
        } catch { return ""; }
    })();

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const meData = await fetchMe();
                if (cancelled) return;
                if (meData?.authenticated) { setMe(meData); return; }
                // Preview-only: if the owner turned on "skip sign-in", auto-login
                // as the owner so the login gate never shows on this environment.
                if (meData?.login_bypass_allowed) {
                    try {
                        const status = await fetchBypassStatus();
                        if (!cancelled && status?.allowed && status?.enabled) {
                            const owner = await devLogin();
                            if (!cancelled) { setMe(owner); return; }
                        }
                    } catch { /* fall through to the normal sign-in gate */ }
                }
                if (!cancelled) setMe(meData || { authenticated: false });
            } catch {
                if (!cancelled) setMe({ authenticated: false });
            }
        })();
        return () => { cancelled = true; };
    }, []);

    // Clean the reset-password URL once the user has either landed on the
    // dashboard OR navigated away from the reset view.
    const goSignin = useCallback(() => {
        setAuthView("signin");
        try {
            if (window.location.pathname === "/reset-password") {
                window.history.replaceState({}, "", "/");
            }
        } catch { /* ignore */ }
    }, []);

    const handleAuthed = useCallback((data) => {
        setMe(data);
        try {
            if (window.location.pathname === "/reset-password") {
                window.history.replaceState({}, "", "/");
            }
        } catch { /* ignore */ }
    }, []);

    if (!me) {
        return (
            <div className="App font-body min-h-screen flex items-center justify-center">
                <p className="font-display text-warm-500 text-sm animate-soft-pulse" data-testid="auth-checking">
                    mochi is waking up… 🍡
                </p>
            </div>
        );
    }
    if (!me.authenticated) {
        if (authView === "reset") {
            return <ResetPasswordScreen token={resetToken} onAuthed={handleAuthed} onBack={goSignin} />;
        }
        if (authView === "forgot") {
            return <ForgotPasswordScreen onBack={goSignin} />;
        }
        return <SignInScreen onAuthed={handleAuthed} onForgotPassword={() => setAuthView("forgot")} />;
    }
    return <MochiApp me={me} />;
}
